/**
 * What plays after the last track of a queue (Gordon 2026-10-31: the end of a
 * queue continues with more music).
 *
 * Andrés 2026-11-01: after a Daddy Boastin album ended, a Damon Frost track
 * played but the Queue still listed the album — the similar tracks were
 * APPENDED after the finished album and a random one started somewhere in the
 * middle. Autoplay now starts a queue of its own: the similar tracks in a
 * shuffled order, nothing from the finished queue, the first one playing.
 */

export interface AutoplayTrack {
  id?: string;
  album?: string;
  artist?: string;
}

/** The continuation queue, in play order. Empty when there's nothing new. */
export function buildAutoplayQueue<T extends AutoplayTrack>(
  finished: T[],
  candidates: T[],
  random: () => number = Math.random,
): T[] {
  const played = new Set(finished.map(t => t?.id).filter(Boolean) as string[]);
  const seen = new Set<string>();
  const fresh: T[] = [];
  for (const t of candidates) {
    if (!t?.id || played.has(t.id) || seen.has(t.id)) continue;
    seen.add(t.id);
    fresh.push(t);
  }
  // Fisher–Yates, so every next track is a surprise but the Queue shows the real order.
  for (let i = fresh.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [fresh[i], fresh[j]] = [fresh[j], fresh[i]];
  }
  return fresh;
}

/** The name the Queue shows for an autoplay queue: "more like <this>". */
export function autoplayBasis(lastPlayed: AutoplayTrack | undefined, previousBasis?: string | null): string {
  return previousBasis || lastPlayed?.album || lastPlayed?.artist || '';
}
